/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import { motion } from 'framer-motion';
import { 
  FiDollarSign,
  FiLayers,
  FiTrendingUp,
  FiShield,
  FiCreditCard,
  FiChevronRight
} from 'react-icons/fi';
import {
  Banknote,
  Building2,
  BarChart4,
  ShieldCheck,
  CreditCard,
} from "lucide-react"; // Replace with your icon library as needed
import Section from '../components/Section';
import Heading from '../ui/PageUI/Heading';
import Subheading from '../ui/PageUI/SubHeading';
import Button from '../components/Button';
import Intro from '../ui/PageUI/Intro';
import Accordion from '../components/Accordion';

const InstitutionsPage = () => {

  const products = [
    {
      title: "Treasury Management",
      description: "Digital asset treasury solutions with multi-tier security",
      icon: <FiDollarSign className="w-6 h-6 text-blue-400" />,
      features: [
        "Hot, Warm and Cold MPC Wallets",
        "DeFi functionality integration",
        "Embedded wallet solutions",
        "Real-time balance reporting"
      ]
    },
    {
      title: "Tokenization",
      description: "End-to-end token creation and distribution platform",
      icon: <FiLayers className="w-6 h-6 text-purple-400" />,
      features: [
        "Create and mint tokens",
        "Custom token economics",
        "Secure distribution mechanisms",
        "Regulatory compliance tools"
      ]
    },
    {
      title: "OTC Trading",
      description: "Institutional-grade trading infrastructure",
      icon: <FiTrendingUp className="w-6 h-6 text-teal-400" />,
      features: [
        "Multiple asset classes",
        "Deep liquidity access",
        "Capital efficient execution",
        "Advanced order types"
      ]
    },
    {
      title: "Custody Solutions",
      description: "Flexible custody solutions tailored to your needs",
      icon: <FiShield className="w-6 h-6 text-green-400" />,
      features: [
        "Self-custody options",
        "Custodial wallet solutions",
        "Hybrid custody models",
        "Insurance-backed storage"
      ]
    },
    {
      title: "Payment Infrastructure",
      description: "Digital asset payment infrastructure",
      icon: <FiCreditCard className="w-6 h-6 text-yellow-400" />,
      features: [
        "Merchant services",
        "Payment processing",
        "Fiat conversion",
        "Settlement solutions"
      ]
    }
  ];

const solutions = [
  {
    title: "Asset Managers & Hedge Funds",
    description: "Secure and scalable infrastructure for digital assets",
    icon: <Banknote className="w-6 h-6 text-blue-500" />,
    features: [
      "MPC-based wallet infrastructure",
      "Real-time NAV and PnL reporting",
      "Automated portfolio rebalancing",
      "Direct access to liquidity venues"
    ]
  },
  {
    title: "Fintechs & Corporates",
    description: "Seamless crypto integration for platforms and treasury",
    icon: <Building2 className="w-6 h-6 text-purple-500" />,
    features: [
      "Multi-chain wallet APIs",
      "Token issuance & management",
      "On/off-ramp integrations",
      "Regulatory & compliance tooling"
    ]
  },
  {
    title: "Financial Institutions",
    description: "Robust trading, settlement, and compliance infrastructure",
    icon: <BarChart4 className="w-6 h-6 text-teal-500" />,
    features: [
      "Access to deep institutional liquidity",
      "Low-latency execution engine",
      "Clearing & net settlement options",
      "Compliance-first architecture (AML/KYC)"
    ]
  },
  {
    title: "Crypto Funds & Family Offices",
    description: "Customizable custody and reporting for digital portfolios",
    icon: <ShieldCheck className="w-6 h-6 text-green-500" />,
    features: [
      "Self & co-managed custody models",
      "Segregated wallet accounts",
      "Advanced user permissioning",
      "Full audit trail & exportable reports"
    ]
  },
  {
    title: "OTC Desks & Liquidity Providers",
    description: "Enterprise-grade APIs and custody for high-volume flows",
    icon: <CreditCard className="w-6 h-6 text-yellow-500" />,
    features: [
      "Custom settlement workflows",
      "Multi-venue liquidity routing",
      "Integrated KYC/KYB checks",
      "24/7 wallet infrastructure with real-time monitoring"
    ]
  }
];


  const clients = [
    { name: "Asset Managers", icon: "📊" },
    { name: "ETF Issuers", icon: "🏦" },
    { name: "FinTechs", icon: "🔄" },
    { name: "Crypto Funds", icon: "🪙" },
    { name: "OTC Desks", icon: "💱" },
    { name: "Trading Firms", icon: "📈" },
    { name: "Token Holders", icon: "🔐" },
    { name: "Financial Institutions", icon: "🏛️" },
    { name: "Investment Platforms", icon: "🖥️" },
    { name: "Broker Dealers", icon: "🤝" }
  ];

  const researchReports = [
    {
      title: "The Rise of Crypto Custody",
      description: "An in-depth look at custodial trends, security standards, and institutional-grade storage requirements.",
      icon: <FiShield className="w-5 h-5" />
    },
    {
      title: "OTC Desk Optimization",
      description: "Learn how large trades are handled off-exchange to reduce slippage and enhance privacy.",
      icon: <FiTrendingUp className="w-5 h-5" />
    },
    {
      title: "Crypto Treasury Strategies",
      description: "Insights on how institutions are managing digital assets on their balance sheets.",
      icon: <FiDollarSign className="w-5 h-5" />
    }
  ];
  const faqsData = [
  {
    question: "How do Asset Managers & Hedge Funds benefit from your platform?",
    answer:
      "We provide secure MPC wallet infrastructure, real-time NAV reporting, and seamless access to institutional liquidity pools, allowing funds to scale confidently while staying compliant."
  },
  {
    question: "What crypto services do you offer for Fintechs and Corporates?",
    answer:
      "We offer APIs for multi-chain wallet integration, token creation and lifecycle management, as well as fiat on/off-ramps and compliance automation to simplify crypto adoption at scale."
  },
  {
    question: "Is your infrastructure suitable for traditional Financial Institutions?",
    answer:
      "Yes, we support institutional-grade execution with low-latency trading, smart routing to deep liquidity pools, and clearing solutions. Our platform is built with compliance and security at its core."
  },
  {
    question: "What custody options are available for Crypto Funds and Family Offices?",
    answer:
      "We support flexible custody models including self-custody, co-managed, and fully custodial setups. Features include granular access controls, audit trails, and insurance-backed storage options."
  },
  {
    question: "How do you support OTC desks and brokers?",
    answer:
      "We provide high-performance APIs, real-time wallet monitoring, custom settlement workflows, and built-in KYC/KYB features — helping OTC desks streamline large-volume trades securely."
  },
  {
    question: "Do you offer compliance tools for regulated institutions?",
    answer:
      "Absolutely. We provide integrated AML/KYC tooling, transaction monitoring, audit logs, and customizable permissioning to ensure you meet global compliance standards."
  }
];


  return (
    <div className=" text-white">
      <Section id="institutions">
        {/* Hero Section */}
        <div className="relative max-w-7xl mx-auto px-6 py-28 text-center">
          <div className="absolute inset-0 overflow-hidden opacity-10">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl opacity-20"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl opacity-20"></div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block px-4 py-1 bg-blue-900/30 rounded-full text-blue-400 text-sm font-medium mb-6 border border-blue-800/50">
              Institutional Solutions
            </div>
            <Heading 
              text="Enterprise-Grade Crypto Infrastructure" 
              className="text-4xl sm:text-5xl font-bold mb-4"
            />
            <Subheading
              text="End-to-end digital asset solutions for financial institutions"
              className="text-xl text-gray-400 max-w-3xl mx-auto"
            />
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
              <Button 
                text="Book Demo" 
                onClick={() => alert('Book Demo')} 
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              />
              <Button 
                text="Get Started" 
                onClick={() => alert('Get Started')} 
                className="bg-transparent border border-gray-600 hover:border-blue-400"
              />
            </div>
          </motion.div>
        </div>

        {/* Product Suite */}
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="text-center mb-16">
            <Heading 
              text="Comprehensive Product Suite" 
              className="text-3xl font-bold mb-4"
            />
            <p className="text-gray-400 max-w-2xl mx-auto">
              Everything your institution needs to operate in digital assets
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true, margin: "-100px" }}
                whileHover={{ y: -5 }}
                className="bg-gray-800/30 rounded-xl border border-gray-700/50 p-8 hover:border-blue-400/30 transition-all h-full"
              >
                <div className="mb-6">
                  {product.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{product.title}</h3>
                <p className="text-gray-400 mb-6">{product.description}</p>
                <ul className="space-y-3">
                  {product.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <div className="text-green-400 mr-3 mt-0.5">✓</div>
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-900/40 to-purple-900/40 p-8 rounded-xl border border-blue-700/30 flex flex-col justify-center"
            >
              <h3 className="text-xl font-semibold mb-3">Custom Solutions</h3>
              <p className="text-gray-300 mb-4">Need something tailored to your specific requirements?</p>
              <p className="text-gray-400 mb-6">Our team can build custom solutions to meet your institutional needs.</p>
              <Button 
                text="Contact Solutions" 
                onClick={() => alert('Contact Solutions')} 
                className="bg-transparent border border-gray-600 hover:border-blue-400"
              />
            </motion.div>
          </div>
        </div>

        {/* Client Types */}
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="text-center mb-16">
            <Heading 
              text="Trusted By Leading Institutions" 
              className="text-3xl font-bold mb-4"
            />
            <p className="text-gray-400 max-w-2xl mx-auto">
              We serve the most sophisticated players in digital assets
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6">
            {clients.map((client, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-gray-800/50 rounded-lg p-6 flex flex-col items-center justify-center hover:bg-gray-700/50 transition-colors"
              >
                <div className="text-2xl mb-3">{client.icon}</div>
                <span className="text-white font-medium text-center">{client.name}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Features Grid */}
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Intro 
            text="Empower your business with secure and scalable crypto solutions." 
            className="text-gray-400 text-lg mb-16 text-center"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Institutional Liquidity",
                description: "Access deep liquidity pools and competitive pricing across all major crypto assets.",
                icon: "💧"
              },
              {
                title: "Regulatory Compliance",
                description: "Built-in compliance tools to meet global regulatory requirements.",
                icon: "🛡️"
              },
              {
                title: "Advanced Trading Tools",
                description: "Professional-grade trading interface with advanced order types and analytics.",
                icon: "📊"
              },
              {
                title: "Secure Custody",
                description: "Institutional-grade custody solutions with insurance coverage.",
                icon: "🔒"
              },
              {
                title: "API Integration",
                description: "Seamless integration with your existing systems and workflows.",
                icon: "⚙️"
              },
              {
                title: "Dedicated Support",
                description: "24/7 access to our institutional client support team.",
                icon: "🛎️"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className="bg-gray-800/30 rounded-xl border border-gray-700/50 p-8 hover:border-blue-400/30 transition-all"
              >
                <div className="text-3xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* User Cases and Solutions */}
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="text-center mb-16">
            <Heading 
              text="Use Cases and Solutions" 
              className="text-3xl font-bold mb-4"
            />
            <p className="text-gray-400 max-w-2xl mx-auto">
              Whatever your needs are, we can help you build the right solution
              for your institution.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true, margin: "-100px" }}
                whileHover={{ y: -5 }}
                className="bg-gray-800/30 rounded-xl border border-gray-700/50 p-8 hover:border-blue-400/30 transition-all h-full"
              >
                <div className="mb-6">
                  {solution.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{solution.title}</h3>
                <p className="text-gray-400 mb-6">{solution.description}</p>
                <ul className="space-y-3">
                  {solution.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <div className="text-green-400 mr-3 mt-0.5">✓</div>
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-900/40 to-purple-900/40 p-8 rounded-xl border border-blue-700/30 flex flex-col justify-center"
            >
              <h3 className="text-xl font-semibold mb-3">Custom Use Cases</h3>
              <p className="text-gray-300 mb-4">Need something tailored to your specific requirements?</p>
              <p className="text-gray-400 mb-6">Our team can build custom solutions to meet your institutional needs.</p>
              <Button 
                text="Contact Solutions" 
                onClick={() => alert('Contact Solutions')} 
                className="bg-transparent border border-gray-600 hover:border-blue-400"
              />
            </motion.div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Heading 
            text="Ready to Get Started?" 
            className="text-3xl font-bold mb-4"
          />
          <p className="text-gray-400 mb-8">
            Our team of experts is here to help you navigate the complexities of the market and discover how 
            Laani's comprehensive solutions can empower your business. Schedule a demo or contact our enterprise team to learn more.


          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              text="Email Enterprise Team" 
              onClick={() => window.location.href = 'mailto:enterprise@laani.io'} 
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            />
            <Button 
              text="Book Consultation" 
              onClick={() => alert('Book Consultation')} 
              className="bg-transparent border border-gray-600 hover:border-blue-400"
            />
          </div>
        </div>

        {/* Research Section */}
        <div className="max-w-7xl mx-auto px-6 py-16 text-center">
          <Heading 
            text="Institutional Research" 
            className="text-3xl font-bold mb-4"
          />
          <p className="text-gray-400 mb-16 max-w-2xl mx-auto">
            Explore our expert-driven publications on market structure, custody evolution, and crypto-native trading strategies.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {researchReports.map((report, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-800/30 rounded-xl border border-gray-700/50 p-8 hover:border-blue-400/30 transition-all text-left"
              >
                <div className="text-blue-400 mb-4">
                  {report.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{report.title}</h3>
                <p className="text-gray-400 mb-6">{report.description}</p>
                <a href="#" className="text-blue-400 hover:text-blue-300 text-sm font-medium flex items-center">
                  Read report <FiChevronRight className="ml-1" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Newsletter */}
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="bg-gray-800/30 rounded-2xl border border-gray-700/50 p-12 text-center">
            <Heading 
              text="Stay Ahead with Our Insights" 
              className="text-2xl font-bold mb-4"
            />
            <p className="text-gray-400 mb-6">
              Subscribe to receive institutional-grade research and market updates.
            </p>
            <form 
              onSubmit={(e) => { e.preventDefault(); alert('Subscribed'); }} 
              className="flex flex-col sm:flex-row gap-4"
            >
              <input
                type="email"
                placeholder="Enter your email"
                required
                className="flex-1 px-4 py-3 rounded-lg bg-gray-700/50 border border-gray-600/50 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button 
                type="submit" 
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w- mx-auto px-6 py-16">
          <Accordion data={faqsData} />
        </div>

      </Section>
    </div>
  );
};

export default InstitutionsPage;
